import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Dialog } from '../Dialog'
import { Field } from '../Field'
import { schoolApi } from '../../api/school'
import { ApiError } from '../../api/client'

type InvitableRole = 'TEACHER' | 'VICE_PRINCIPAL' | 'IT_ADMIN'

/**
 * Диалог приглашения учителя в школу. Сначала форма выбора роли;
 * после создания токена — экран с самим токеном (копировать в буфер).
 */
export function InviteDialog({
  schoolId,
  onClose,
}: {
  schoolId: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [role, setRole] = useState<InvitableRole>('TEACHER')
  const [token, setToken] = useState<string | null>(null)
  const mut = useMutation({
    mutationFn: () => schoolApi.createInvite(schoolId, role),
    onSuccess: (data) => setToken(data.token),
  })

  if (token) return <TokenView token={token} onClose={onClose} />

  return (
    <Dialog title={t('inviteDialog.title')} onClose={onClose}>
      <Field label={t('inviteDialog.roleLabel')}>
        <select
          className="input-field"
          value={role}
          onChange={(e) => setRole(e.target.value as InvitableRole)}
        >
          <option value="TEACHER">{t('inviteDialog.roleTeacher')}</option>
          <option value="VICE_PRINCIPAL">{t('inviteDialog.roleVicePrincipal')}</option>
          <option value="IT_ADMIN">{t('inviteDialog.roleItAdmin')}</option>
        </select>
      </Field>
      {mut.error && (
        <div className="text-sm text-red-600 mb-3">
          {(mut.error as ApiError).message}
        </div>
      )}
      <div className="flex gap-2 justify-end mt-3">
        <button onClick={onClose} className="btn-outline text-sm">{t('common.cancel')}</button>
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="btn-primary text-sm"
        >
          {mut.isPending ? '…' : t('inviteDialog.createToken')}
        </button>
      </div>
    </Dialog>
  )
}

function TokenView({ token, onClose }: { token: string; onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <Dialog title={t('inviteDialog.createdTitle')} onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">
        {t('inviteDialog.createdHint')}
      </p>
      <div className="bg-gray-50 border border-gray-200 rounded p-3 font-mono text-sm break-all mb-3 select-all">
        {token}
      </div>
      <p className="text-xs text-gray-500">{t('inviteDialog.validity')}</p>
      <div className="flex gap-2 justify-end mt-4">
        <button
          onClick={() => navigator.clipboard.writeText(token)}
          className="btn-outline text-sm"
        >
          📋 {t('common.copy')}
        </button>
        <button onClick={onClose} className="btn-primary text-sm">{t('common.close')}</button>
      </div>
    </Dialog>
  )
}
